import React, { useState, useRef } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { useCompany } from "@/context/CompanyContext";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, FileSpreadsheet, FileSignature, Database,
  CheckCircle2, AlertCircle, Trash2, Plus, ArrowRight, ArrowLeft,
  Calendar, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const CHF = new Intl.NumberFormat("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (n) => CHF.format(n || 0);

const KIND_LABELS = {
  balance: "Bilanz",
  income_statement: "Erfolgsrechnung",
  mixed: "Gemischte Saldoliste",
  unknown: "Unbekannt",
};

// Plausibilitätsprüfung — erkennt typische Anomalien in den Vortragswerten
function checkPlausibility(lines) {
  const warnings = [];
  for (const ln of lines) {
    const code = String(ln.code || "").trim();
    const name = String(ln.name || "");
    const debit = Number(ln.debit) || 0;
    const credit = Number(ln.credit) || 0;
    if (!/^\d{3,6}/.test(code)) continue;
    const num = parseInt(code.slice(0, 4), 10);
    const label = `${code} ${name}`.trim();
    if (num >= 1000 && num <= 1019 && credit > 0.005) {
      warnings.push({
        severity: "error",
        account: label,
        message: `Kasse ist negativ (−${fmt(credit)}). Eine Kasse kann physisch nie unter 0 fallen — vermutlich fehlen Einnahme-Buchungen in der Altbuchhaltung.`,
      });
    } else if (num >= 1020 && num <= 1069 && credit > 0.005) {
      warnings.push({
        severity: "warning",
        account: label,
        message: `Bank-/Postkonto im Minus (−${fmt(credit)}). Prüfe, ob alle Bankauszüge verbucht wurden oder ob ein Kontokorrentkredit besteht.`,
      });
    } else if (num >= 1090 && num <= 1099 && Math.abs(debit - credit) > 0.005) {
      warnings.push({
        severity: "warning",
        account: label,
        message: `Durchlauf-/Transferkonto weist einen Saldo von ${fmt(debit - credit)} auf — sollte per Stichtag ausgeglichen (0) sein.`,
      });
    } else if (num >= 1100 && num <= 1199 && credit > 0.005) {
      warnings.push({
        severity: "warning",
        account: label,
        message: `Debitoren mit Haben-Saldo (−${fmt(credit)}) — üblicherweise ein Soll-Konto. Vorauszahlungen von Kunden gehören auf 2030.`,
      });
    }
  }
  return warnings;
}

export default function OpeningBalance() {
  const { company, setCompany } = useCompany();
  const cid = company?._id;
  const onboarding = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("onboarding") === "chart";
  const [setupDone, setSetupDone] = useState(false);
  const markSetupDone = async () => {
    try {
      await apiClient.post(`/companies/${cid}/setup-onboarding/done`);
      setSetupDone(true);
      setCompany?.({ ...company, setup_onboarding_done: true });
    } catch { /* still show success */ }
  };
  const [step, setStep] = useState(1);
  const [scope, setScope] = useState("balance"); // balance | pl | both
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [lines, setLines] = useState([]);
  const [parseInfo, setParseInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [docsStatus, setDocsStatus] = useState({}); // { balance: filename, income_statement: filename }
  const fileRef = useRef(null);

  if (!cid) {
    return (
      <Card className="p-8 text-center">
        <p className="text-slate-500">Bitte zuerst eine Firma auswählen.</p>
      </Card>
    );
  }

  // ---- Upload (unterstützt mehrere Dateien, z.B. Bilanz + Erfolgsrechnung) ----
  const parseOneFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await apiClient.post(
      `/companies/${cid}/opening-balance/parse-file`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return r.data;
  };

  const processFiles = async (fileList, { merge = false } = {}) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    try {
      const keepExisting = merge || (scope === "both" && lines.length > 0);
      const existing = keepExisting ? [...lines] : [];
      const byCode = new Map(existing.map((l) => [String(l.code), l]));
      const newDocs = { ...docsStatus };
      let added = 0;
      let skipped = 0;
      let rejected = 0;
      const formats = [];
      for (const file of files) {
        const data = await parseOneFile(file);
        const kind = data.document_kind || "unknown";
        const kindLabel = KIND_LABELS[kind] || "Unbekannt";
        // Dokumenttyp gegen gewählten Umfang validieren
        if (scope === "balance" && kind === "income_statement") {
          toast.error(
            `❌ «${file.name}» ist eine ERFOLGSRECHNUNG — du hast aber «Nur Bilanz» gewählt. Datei nicht übernommen.`,
            { duration: 8000 },
          );
          rejected += 1;
          continue;
        }
        if (scope === "pl" && kind === "balance") {
          toast.error(
            `❌ «${file.name}» ist eine BILANZ — du hast aber «Nur Erfolgsrechnung» gewählt. Datei nicht übernommen.`,
            { duration: 8000 },
          );
          rejected += 1;
          continue;
        }
        if ((data.lines || []).length === 0) {
          toast.warning(`«${file.name}»: Keine Konten erkannt — Datei prüfen.`);
          rejected += 1;
          continue;
        }
        // Stichtag im PDF vs. gewählter Übernahme-Stichtag
        if (data.statement_date) {
          const diffDays = Math.abs(
            (new Date(data.statement_date) - new Date(effectiveDate)) / 86400000,
          );
          const fmtCH = (iso) => new Date(iso).toLocaleDateString("de-CH");
          if (diffDays > 2) {
            toast.warning(
              `⚠️ «${file.name}» ist per ${fmtCH(data.statement_date)} — dein Übernahme-Stichtag ist aber ${fmtCH(effectiveDate)}. Richtiges Jahr gewählt?`,
              {
                duration: 12000,
                action: {
                  label: `Stichtag ${fmtCH(data.statement_date)} übernehmen`,
                  onClick: () => setEffectiveDate(data.statement_date),
                },
              },
            );
          }
        }
        // Typ-Feedback + Duplikat-Hinweis
        if (kind === "balance" || kind === "income_statement") {
          if (scope === "both" && newDocs[kind] && newDocs[kind] !== file.name) {
            toast.warning(
              `«${file.name}» ist erneut eine ${kindLabel} — «${newDocs[kind]}» wurde bereits hochgeladen. Konten werden zusammengeführt.`,
            );
          } else {
            toast.success(`✓ «${file.name}»: ${kindLabel} erkannt — richtige Datei.`);
          }
          newDocs[kind] = file.name;
        } else if (kind === "mixed") {
          toast.info(`«${file.name}»: Gemischte Saldoliste (Bilanz + Erfolgsrechnung) erkannt.`);
          newDocs.balance = newDocs.balance || file.name;
          newDocs.income_statement = newDocs.income_statement || file.name;
        }
        formats.push(data.format_detected.toUpperCase());
        for (const ln of data.lines || []) {
          const code = String(ln.code);
          if (byCode.has(code)) {
            skipped += 1;
            continue;
          }
          byCode.set(code, ln);
          added += 1;
        }
      }
      setDocsStatus(newDocs);
      if (added === 0) {
        if (rejected === 0) {
          toast.warning(
            skipped > 0
              ? `Keine neuen Konten — ${skipped} Konten waren bereits in der Tabelle.`
              : "Keine Konten erkannt. Prüfe die Datei oder gib manuell ein.",
          );
        }
        return;
      }
      let mergedLines = Array.from(byCode.values());
      // Bilanz + Erfolgsrechnung zusammen: Gewinn wäre doppelt (2979 UND Netto der Erfolgskonten).
      const plNet = mergedLines
        .filter((l) => /^[3-9]/.test(String(l.code)))
        .reduce((s, l) => s + (Number(l.credit) || 0) - (Number(l.debit) || 0), 0);
      if (Math.abs(plNet) > 0.01) {
        const dup = mergedLines.find(
          (l) => String(l.code) === "2979" &&
            Math.abs((Number(l.credit) || 0) - (Number(l.debit) || 0) - plNet) < 0.05,
        );
        if (dup) {
          mergedLines = mergedLines.filter((l) => l !== dup);
          toast.info(
            "Gewinn-Zeile (2979) entfernt — das Jahresergebnis ist bereits in den Erfolgskonten enthalten.",
          );
        }
      }
      const sumD = mergedLines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
      const sumC = mergedLines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
      setLines(mergedLines);
      setParseInfo({
        format_detected: formats.join(" + "),
        lines_count: mergedLines.length,
        sum_debit: sumD,
        sum_credit: sumC,
      });
      // Bei «Alles»: warten, bis BEIDE Dokumente hochgeladen sind
      if (scope === "both" && step === 2 && !(newDocs.balance && newDocs.income_statement)) {
        const missing = newDocs.balance ? "Erfolgsrechnung" : "Bilanz";
        toast.info(`⏳ Es fehlt noch die ${missing} — bitte auch diese hochladen.`, { duration: 8000 });
        return;
      }
      toast.success(
        `${added} Konten ${keepExisting ? "hinzugefügt" : "erkannt"} (${formats.join(" + ")})` +
        (skipped > 0 ? ` · ${skipped} Duplikate übersprungen` : "") +
        ` · Soll ${fmt(sumD)} · Haben ${fmt(sumC)}`,
      );
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Datei konnte nicht gelesen werden");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleUpload = (e) => processFiles(e.target.files, { merge: step === 3 });

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;
    processFiles(e.dataTransfer?.files, { merge: step === 3 });
  };

  // ---- Manual entry ----
  const addLine = () => setLines((l) => [...l, { code: "", name: "", debit: 0, credit: 0 }]);
  const updateLine = (idx, key, val) =>
    setLines((l) => l.map((row, i) => (i === idx ? { ...row, [key]: val } : row)));
  const removeLine = (idx) => setLines((l) => l.filter((_, i) => i !== idx));

  const sumDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const sumCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const diff = sumDebit - sumCredit;
  const isBalanced = Math.abs(diff) < 0.01;
  const plausibilityWarnings = checkPlausibility(lines);

  // ---- Post ----
  const handlePost = async () => {
    if (!isBalanced) {
      if (!window.confirm(
        `Soll (${fmt(sumDebit)}) ≠ Haben (${fmt(sumCredit)}). ` +
        `Differenz: ${fmt(diff)}\n\nTrotzdem fortfahren? (Wird abgelehnt.)`,
      )) return;
    }
    setPosting(true);
    try {
      const cleanLines = lines
        .filter((l) => (l.code || "").toString().trim())
        .map((l) => ({
          code: String(l.code).trim(),
          name: String(l.name || "").trim(),
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        }));
      const r = await apiClient.post(
        `/companies/${cid}/opening-balance/post`,
        {
          lines: cleanLines,
          effective_date: effectiveDate,
          scope,
          create_missing_accounts: true,
          description: `Eröffnungsbilanz / Saldovortrag per ${effectiveDate}`,
        },
      );
      toast.success(r.data.message);
      setStep(4);
      if (onboarding && !company?.setup_onboarding_done) await markSetupDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Buchung fehlgeschlagen");
    } finally {
      setPosting(false);
    }
  };

  // ---- Step UI ----
  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="opening-balance-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Eröffnungsbilanz / Saldovortrag
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Übernehme die Salden aus deiner alten Buchhaltung (CSV · XLSX · PDF · Banana).
          KI-gestützt für PDFs. Soll = Haben wird automatisch geprüft.
        </p>
      </div>

      {onboarding && !company?.setup_onboarding_done && !setupDone && (
        <div className="rounded-lg border border-indigo-300 bg-white p-4" data-testid="opening-onboarding">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Schritt 2 von 2 · Neue Firma</div>
          <h3 className="text-base font-semibold text-slate-900 mt-0.5">Saldi der alten Buchhaltung übernehmen</h3>
          <p className="text-xs text-slate-600 mt-1">
            Laden Sie die Schlussbilanz (und optional die Erfolgsrechnung) des letzten Geschäftsjahres hoch – die Saldi werden als Eröffnungsbuchung per Stichtag eingebucht,
            fehlende Konten werden automatisch angelegt. Damit passt die neue Buchhaltung nahtlos an die alte an.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
            <a href="/settings?onboarding=chart#section-kontenrahmen" className="inline-flex items-center gap-1 text-slate-500 hover:underline" data-testid="opening-onboarding-back">
              <ArrowLeft className="h-3 w-3" /> Zurück zu Schritt 1 (Kontenplan)
            </a>
            <button type="button" onClick={markSetupDone} className="ml-auto text-indigo-700 underline-offset-2 hover:underline" data-testid="opening-onboarding-skip">
              Neue Firma ohne Vorgeschichte – Einrichtung abschliessen
            </button>
          </div>
        </div>
      )}
      {setupDone && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="opening-onboarding-done">
          <CheckCircle2 className="h-4 w-4" /> Einrichtung abgeschlossen – Kontenplan und Eröffnungsbilanz sind bereit. Viel Erfolg mit der neuen Buchhaltung!
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => (window.location.href = "/")} data-testid="opening-onboarding-dashboard">Zum Dashboard</Button>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {[
          { n: 1, label: "Stichtag" },
          { n: 2, label: "Daten laden" },
          { n: 3, label: "Prüfen & Bearbeiten" },
          { n: 4, label: "Fertig" },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
                step === s.n
                  ? "bg-slate-900 text-white"
                  : step > s.n
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-100 text-slate-500"
              }`}
            >
              <span className="font-mono font-bold">{s.n}.</span> {s.label}
              {step > s.n && <CheckCircle2 className="h-3 w-3" />}
            </div>
            {i < arr.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Stichtag + Scope */}
      {step === 1 && (
        <Card className="p-6 space-y-4" data-testid="opening-balance-step1">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Stichtag und Umfang wählen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Stichtag der Übernahme</Label>
              <SwissDateField
                
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                data-testid="opening-balance-date"
              />
              <p className="text-[11px] text-slate-500">
                Buchungen ab dem Tag NACH dem Stichtag laufen im neuen System.
                Typisch: 31.12. des Vorjahres.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Welche Konten übernehmen?</Label>
              <div className="space-y-1.5">
                {[
                  { v: "balance", label: "Nur Bilanz (1xxx Aktiva + 2xxx Passiva)", hint: "Jahreswechsel" },
                  { v: "pl", label: "Nur Erfolgsrechnung (3xxx - 9xxx)", hint: "selten" },
                  { v: "both", label: "Alles (Bilanz + Erfolgsrechnung)", hint: "Unterjähriger Wechsel" },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${
                      scope === opt.v ? "border-slate-900 bg-slate-50" : "border-slate-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="scope"
                      value={opt.v}
                      checked={scope === opt.v}
                      onChange={() => setScope(opt.v)}
                      className="mt-0.5"
                      data-testid={`opening-balance-scope-${opt.v}`}
                    />
                    <div className="text-sm">
                      <div className="font-medium text-slate-900">{opt.label}</div>
                      <div className="text-[11px] text-slate-500">{opt.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => { setDocsStatus({}); setStep(2); }}
              data-testid="opening-balance-next-1"
            >
              Weiter <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2: Upload or Manual */}
      {step === 2 && (
        <Card className="p-6 space-y-4" data-testid="opening-balance-step2">
          <h2 className="font-semibold text-slate-900">Daten laden</h2>
          {scope === "both" && (
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex flex-wrap gap-x-8 gap-y-2 text-sm"
              data-testid="opening-balance-doc-checklist"
            >
              <div className="flex items-center gap-2" data-testid="opening-balance-doc-balance">
                {docsStatus.balance ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <span className="h-4 w-4 rounded-full border-2 border-slate-300 inline-block" />
                )}
                <span className={docsStatus.balance ? "text-emerald-800 font-medium" : "text-slate-600"}>
                  Bilanz {docsStatus.balance ? `✓ (${docsStatus.balance})` : "— fehlt noch"}
                </span>
              </div>
              <div className="flex items-center gap-2" data-testid="opening-balance-doc-income">
                {docsStatus.income_statement ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <span className="h-4 w-4 rounded-full border-2 border-slate-300 inline-block" />
                )}
                <span className={docsStatus.income_statement ? "text-emerald-800 font-medium" : "text-slate-600"}>
                  Erfolgsrechnung {docsStatus.income_statement ? `✓ (${docsStatus.income_statement})` : "— fehlt noch"}
                </span>
              </div>
              <div className="w-full text-[11px] text-slate-500">
                Du hast «Alles» gewählt — lade beide Dokumente hoch (einzeln oder zusammen).
                Es geht erst weiter, wenn beide erkannt wurden.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
              onDrop={handleDrop}
              disabled={uploading}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? "border-violet-500 bg-violet-50"
                  : "border-slate-300 hover:border-slate-900"
              }`}
              data-testid="opening-balance-upload-btn"
            >
              <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? "text-violet-500" : "text-slate-400"}`} />
              <div className="font-medium text-slate-900">
                {uploading ? "Wird verarbeitet…" : dragActive ? "Datei hier loslassen" : "Datei hochladen oder hierher ziehen"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                CSV · XLSX · PDF · Banana (.ac2) — auch mehrere Dateien
                (z.B. Bilanz + Erfolgsrechnung) auf einmal
              </div>
              <div className="mt-3 flex items-center justify-center gap-3 text-xs text-slate-400">
                <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> CSV</span>
                <span className="flex items-center gap-1"><FileSpreadsheet className="h-3 w-3" /> XLSX</span>
                <span className="flex items-center gap-1"><FileSignature className="h-3 w-3" /> PDF</span>
                <span className="flex items-center gap-1"><Database className="h-3 w-3" /> Banana</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full text-[10px]">
                <Sparkles className="h-2.5 w-2.5" /> PDF: KI-gestützt
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setLines([{ code: "", name: "", debit: 0, credit: 0 }]); setStep(3); }}
              className="border border-slate-300 hover:border-slate-900 rounded-lg p-8 text-center transition-colors"
              data-testid="opening-balance-manual-btn"
            >
              <Plus className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <div className="font-medium text-slate-900">Manuell eingeben</div>
              <div className="text-xs text-slate-500 mt-1">
                Konto für Konto in einer Tabelle erfassen
              </div>
            </button>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
            </Button>
          </div>
        </Card>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept=".csv,.xlsx,.xls,.pdf,.ac2,.sba,.txt"
        onChange={handleUpload}
        className="hidden"
        data-testid="opening-balance-file-input"
      />

      {/* Step 3: Edit table */}
      {step === 3 && (
        <Card className="p-6 space-y-4" data-testid="opening-balance-step3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-semibold text-slate-900">Konten prüfen und anpassen</h2>
            <div className="flex items-center gap-2 text-xs">
              {parseInfo && (
                <Badge variant="secondary" className="bg-slate-100">
                  Quelle: {parseInfo.format_detected.toUpperCase()} · {parseInfo.lines_count} Zeilen
                </Badge>
              )}
              {parseInfo?.missing_accounts?.length > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                  {parseInfo.missing_accounts.length} neue Konten werden angelegt
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="opening-balance-add-file-btn"
              >
                <Upload className="h-3.5 w-3.5 mr-1" />
                {uploading ? "Wird verarbeitet…" : "Weitere Datei hinzufügen"}
              </Button>
            </div>
          </div>

          {plausibilityWarnings.length > 0 && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
              data-testid="opening-balance-warnings"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <AlertCircle className="h-4 w-4" />
                Plausibilitätsprüfung: {plausibilityWarnings.length} Auffälligkeit
                {plausibilityWarnings.length !== 1 ? "en" : ""} gefunden
              </div>
              <ul className="space-y-1.5">
                {plausibilityWarnings.map((w, i) => (
                  <li
                    key={i}
                    className={`text-xs flex gap-2 ${
                      w.severity === "error" ? "text-red-800" : "text-amber-800"
                    }`}
                    data-testid={`opening-balance-warning-${i}`}
                  >
                    <span className="flex-shrink-0">{w.severity === "error" ? "🔴" : "🟡"}</span>
                    <span>
                      <span className="font-mono font-semibold">{w.account}</span>: {w.message}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700">
                Du kannst trotzdem buchen — die Hinweise betreffen die Qualität der Quelldaten,
                nicht den Import selbst.
              </p>
            </div>
          )}

          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-2 py-2 text-left w-24">Konto-Nr.</th>
                  <th className="px-2 py-2 text-left">Bezeichnung</th>
                  <th className="px-2 py-2 text-right w-32">Soll</th>
                  <th className="px-2 py-2 text-right w-32">Haben</th>
                  <th className="px-2 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {lines.map((row, idx) => (
                  <tr key={idx} className="border-t" data-testid={`opening-balance-row-${idx}`}>
                    <td className="px-2 py-1">
                      <Input
                        type="text"
                        value={row.code}
                        onChange={(e) => updateLine(idx, "code", e.target.value)}
                        className="h-8 text-sm font-mono"
                        placeholder="1020"
                        data-testid={`opening-balance-code-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateLine(idx, "name", e.target.value)}
                        className="h-8 text-sm"
                        placeholder="Bank PostFinance"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.debit}
                        onChange={(e) => updateLine(idx, "debit", e.target.value)}
                        className="h-8 text-sm text-right"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.credit}
                        onChange={(e) => updateLine(idx, "credit", e.target.value)}
                        className="h-8 text-sm text-right"
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => removeLine(idx)}
                        className="text-slate-400 hover:text-rose-600"
                        title="Zeile entfernen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                <tr>
                  <td colSpan={2} className="px-2 py-2 text-right">Summen:</td>
                  <td className="px-2 py-2 text-right font-mono" data-testid="opening-balance-sum-debit">
                    {fmt(sumDebit)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono" data-testid="opening-balance-sum-credit">
                    {fmt(sumCredit)}
                  </td>
                  <td />
                </tr>
                <tr>
                  <td colSpan={2} className="px-2 py-1 text-right text-xs text-slate-500">Differenz:</td>
                  <td colSpan={2} className={`px-2 py-1 text-right font-mono text-xs ${
                    isBalanced ? "text-emerald-700" : "text-rose-700"
                  }`}>
                    {isBalanced ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Balanced
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {fmt(diff)}
                      </span>
                    )}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={addLine} data-testid="opening-balance-add-row">
              <Plus className="h-3.5 w-3.5 mr-1" /> Zeile hinzufügen
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
              </Button>
              <Button
                onClick={handlePost}
                disabled={posting || lines.length === 0 || !isBalanced}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="opening-balance-post-btn"
              >
                {posting ? "Bucht…" : `Eröffnungsbilanz buchen (${effectiveDate})`}
              </Button>
            </div>
          </div>
          {!isBalanced && lines.length > 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Soll und Haben müssen identisch sein. Differenz: {fmt(diff)}.
                Tipp: trage die Differenz auf Konto 2800 (Eigenkapital) oder 2979 (Gewinnvortrag) ein.
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Step 4: Done */}
      {step === 4 && (
        <Card className="p-8 text-center space-y-4 bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-emerald-200" data-testid="opening-balance-step4">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
          <h2 className="text-2xl font-semibold text-slate-900">Eröffnungsbilanz gebucht!</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            Per <strong>{effectiveDate}</strong> wurden alle Saldovorträge gebucht.
            Ab dem nächsten Tag kannst du normal weiterbuchen.
          </p>
          <div className="flex justify-center gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setStep(1);
                setLines([]);
                setParseInfo(null);
              }}
            >
              Weitere Buchhaltung übernehmen
            </Button>
            <Button onClick={() => (window.location.href = "/journal")}>
              Zum Journal
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
